import { Link } from 'react-router-dom';
import {
  ADMINISTRATOR,
  IAM_EDITOR,
  CATALOG_ROLES,
  CONTENT_ROLES,
  SALES_ROLES,
  TOTVS_INTEGRATOR_ROLES,
  hasAnyRole,
} from '@/shared/guards/roles';
import { useEffectiveRoles } from '@/shared/view-as/use-effective-roles';

/** Entidades que têm tela própria — só essas viram link. */
export type EntityKind =
  | 'user'
  | 'transaction'
  | 'contract'
  | 'billingAgreement'
  | 'debtArrangement'
  | 'offer'
  | 'product'
  | 'contentItem'
  /** O id é o da **questão**: o comentário se lê e se edita na ficha dela. */
  | 'subjectView'
  | 'totvsTask';

/**
 * Para onde cada entidade aponta e quais papéis conseguem abrir a tela. Os
 * papéis espelham o `RoleGuard` da rota de destino: sem eles o link levaria a
 * um redirect pra `/`, então nesses casos o id é renderizado como texto puro.
 */
const ENTITY_TARGETS: Record<
  EntityKind,
  { to: (id: string) => string; roles: readonly string[] }
> = {
  user: {
    to: (id) => `/management/users/${id}`,
    // Espelha o `RoleGuard` de `/management/users/:userId` (`users/presentation/routes.tsx`),
    // que aceita os dois. Faltando o `IAM_EDITOR` aqui, o link nascia como
    // texto morto justamente para o papel criado para operar acessos.
    roles: [ADMINISTRATOR, IAM_EDITOR],
  },
  transaction: {
    to: (id) => `/sales/${id}`,
    roles: SALES_ROLES,
  },
  contract: {
    to: (id) => `/contracts/${id}`,
    roles: SALES_ROLES,
  },
  billingAgreement: {
    to: (id) => `/billing-agreements/${id}`,
    roles: SALES_ROLES,
  },
  debtArrangement: {
    to: (id) => `/debt-arrangements/${id}`,
    roles: SALES_ROLES,
  },
  offer: {
    to: (id) => `/offers/${id}`,
    roles: SALES_ROLES,
  },
  product: {
    to: (id) => `/management/catalog/products/${id}`,
    roles: CATALOG_ROLES,
  },
  contentItem: {
    to: (id) => `/content/items/${id}`,
    roles: CONTENT_ROLES,
  },
  subjectView: {
    to: (id) => `/content/views/${id}`,
    roles: CONTENT_ROLES,
  },
  totvsTask: {
    to: (id) => `/totvs/jobs/${id}`,
    roles: TOTVS_INTEGRATOR_ROLES,
  },
};

interface EntityLinkProps {
  kind: EntityKind;
  /** Id da entidade. Vazio/nulo cai no fallback (não vira link). */
  id: string | null | undefined;
  /** Texto exibido. Padrão: o próprio id. */
  children?: React.ReactNode;
  /** Exibido quando não há id. */
  fallback?: React.ReactNode;
  /**
   * Abre em outra aba. Usado quando o link sai de dentro de um modal: navegar
   * na mesma aba fecharia o modal e perderia o lugar em que se estava lendo.
   */
  newTab?: boolean;
}

/**
 * Id de uma entidade que tem tela própria, renderizado como link para ela.
 *
 * Degrada para texto puro quando não há id ou quando o usuário não tem papel
 * para abrir a tela de destino — assim a informação continua visível sem
 * oferecer um caminho que terminaria em redirect.
 *
 * Não traz estilo de fonte: envolva com o `Mono` do módulo para manter o
 * visual monoespaçado já usado nos ids.
 */
export function EntityLink({
  kind,
  id,
  children,
  fallback,
  newTab = false,
}: EntityLinkProps) {
  const roles = useEffectiveRoles();
  const target = ENTITY_TARGETS[kind];
  const canOpen = hasAnyRole(roles, target.roles);

  if (!id) return <>{fallback ?? '—'}</>;
  if (!canOpen) return <>{children ?? id}</>;

  return (
    <Link
      to={target.to(id)}
      // Ids aparecem dentro de linhas clicáveis (`onRowClick`); sem isso o
      // clique no link dispararia também a ação da linha.
      onClick={(e) => e.stopPropagation()}
      target={newTab ? '_blank' : undefined}
      rel={newTab ? 'noopener noreferrer' : undefined}
      className="text-(--color-accent) hover:underline"
    >
      {children ?? id}
    </Link>
  );
}
